/**
 * BetSlip Utility Functions
 *
 * Shared transformation utilities for BetSlip wrapper components.
 */

import type { BetSlipChip } from '@/types/betslip.types';
import { formatDollarPrice } from '@/utils/formatPrice';

/**
 * Mode type for BetSlip components
 */
export type BetSlipMode = 'conid' | 'product' | 'none';

/**
 * Determine BetSlip mode based on conid/productConid presence
 * Conid takes precedence over productConid
 * @param conid - Single contract ID
 * @param productConid - Election product ID
 * @returns The appropriate mode
 */
export function determineBetSlipMode(conid: number | undefined, productConid: number | undefined): BetSlipMode {
  if (conid) return 'conid';
  if (productConid) return 'product';
  return 'none';
}

/**
 * Party colors for candidate display
 */
const PARTY_COLORS: Record<string, string> = {
  REP: '#b01f14', // Republican red
  REPUBLICAN: '#b01f14',
  DEM: '#1a37c4', // Democrat blue
  DEMOCRAT: '#1a37c4',
  IND: '#808080', // Independent gray
  INDEPENDENT: '#808080',
};

/**
 * Format decimal price (0-1 range) to dollar string
 * @param price - Price as decimal (e.g., 0.47 = $0.47), null if no market
 * @returns Formatted price string (e.g., "0.47") or "\u2014" if null/undefined
 */
export function formatDecimalPrice(price: number | null | undefined): string {
  return formatDollarPrice(price);
}

/**
 * Get the display color for a political party
 * @param party - Party abbreviation (REP, DEM, IND)
 * @returns Hex color string or undefined for unknown parties
 */
export function getPartyColor(party: string | undefined): string | undefined {
  if (!party) return undefined;
  return PARTY_COLORS[party.toUpperCase()];
}

/**
 * Create YES/NO price chips from decimal bid prices (0-1 range)
 * @param yesBid - Yes bid price as decimal (e.g., 0.47), null if no market
 * @param noBid - No bid price as decimal (e.g., 0.53), null if no market
 * @returns Array of chips with formatted prices
 */
export function createBidPriceChips(
  yesBid: number | null | undefined,
  noBid: number | null | undefined,
): BetSlipChip[] {
  return [{ label: `YES $${formatDecimalPrice(yesBid)}` }, { label: `NO $${formatDecimalPrice(noBid)}` }];
}
